import { Controller } from '@hotwired/stimulus';

// This controller can be used to toggle classes on the element it is attached to
// or other elements that are attached to the same controller via outlets. For example:
//
// <button
//   data-controller="toggle-class-list"
//   data-toggle-class-list-on-class-list-value="some-cool-class"
//   data-toggle-class-list-off-class-list-value="turn-me-off"
//   data-action="click->toggle-class-list#toggle"
//  >
//
// Or with outlets:
//
// <input
//   type="checkbox"
//   data-controller="toggle-class-list"
//   data-toggle-class-list-toggle-class-list-outlet=".stuff-to-hide"
//   data-action="change->toggle-class-list#toggle"
// />
//
//  <div
//    class="stuff-to-hide"
//    data-controller="toggle-class-list"
//    data-toggle-class-list-off-class-list-value="hidden"
//    data-toggle-class-list-start-toggled-on-value="true"
//   />
//
// Additionally, the selectorValue and triggerEventNameValue can be used to
// bind to events to a child of the element. This can be useful for working
// with sage components.
export default class extends Controller {
  static outlets = ['toggle-class-list'];

  static values = {
    startToggledOn: Boolean,
    onClassList: String,
    offClassList: String,
    selector: String,
    triggerEventName: String,
    toggleOnload: {
      type: Boolean,
      default: true,
    },
    explicitToggle: {
      type: Boolean,
      default: false,
    },
  };

  connect() {
    this.toggledOn = this.startToggledOnValue;
    this.onClassList = this.onClassListValue.split(/\s+/).filter((item) => item);
    this.offClassList = this.offClassListValue.split(/\s+/).filter((item) => item);

    if (this.hasSelectorValue) {
      this.toggledElement = this.element.querySelector(this.selectorValue);
    } else {
      this.toggledElement = this.element;
    }

    this.boundOnToggle = this.toggle.bind(this);
    if (this.hasTriggerEventNameValue) {
      this.toggledElement.addEventListener(this.triggerEventNameValue, this.boundOnToggle);
    }

    if (this.toggleOnloadValue) {
      this.toggle();
    }
  }

  disconnect() {
    if (this.hasTriggerEventNameValue) {
      this.toggledElement.removeEventListener(this.triggerEventNameValue, this.boundOnToggle);
    }
  }

  toggle() {
    if (this.explicitToggleValue) {
      return;
    }
    if (this.toggledElement.checked !== undefined) {
      // Support checkboxes specifically since it is a common pattern to use
      // them to toggle the CSS of other elements.
      if (this.toggledElement.checked === true) {
        this.toggleOn();
      } else {
        this.toggleOff();
      }
    } else if (this.toggledOn) {
      this.toggleOff();
    } else {
      this.toggleOn();
    }
  }

  toggleOn() {
    this.toggledOn = true;

    this.offClassList.forEach((item) => this.element.classList.remove(item));
    this.onClassList.forEach((item) => this.element.classList.add(item));

    this.toggleClassListOutlets.forEach((outlet) => outlet.toggleOn());
  }

  toggleOff() {
    this.toggledOn = false;

    this.onClassList.forEach((item) => this.element.classList.remove(item));
    this.offClassList.forEach((item) => this.element.classList.add(item));

    this.toggleClassListOutlets.forEach((outlet) => outlet.toggleOff());
  }
}
